import { FeatureAccessError } from "@/lib/billing/feature-access";
import { HttpError } from "@/lib/api/safe-fetch";

export interface PlanRestrictionError {
  status: 402 | 403 | 429;
  feature: string;
  planRequired?: string;
  message?: string;
  /** Detailed error information from the API */
  details?: FeatureAccessError;
  /** The body's errorType, when the restriction came from one */
  errorType?: string;
  /** RESOURCE_LIMIT_EXCEEDED: which resource ("prompts", "engines") */
  resource?: string;
  /** INSUFFICIENT_CREDITS: what the action costs and the org's balance */
  creditsNeeded?: number;
  balance?: number;
  /** False when credits can't get past this (a free org can't buy any) */
  creditsEligible?: boolean;
}

/**
 * Check if an error or response indicates a plan restriction (403 or 429)
 * Returns parsed error details if it's a plan restriction, null otherwise
 */
export function isPlanRestrictionError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
): PlanRestrictionError | null {
  // Handle Response objects
  if (error instanceof Response) {
    if (error.status === 403 || error.status === 429) {
      return {
        status: error.status as 403 | 429,
        feature: extractFeatureFromUrl(error.url) || "this feature",
        message: error.statusText,
      };
    }
  }

  // Handle Error objects with response property (from fetch)
  if (error?.response?.status === 403 || error?.response?.status === 429) {
    return {
      status: error.response.status as 403 | 429,
      feature: extractFeatureFromUrl(error.response.url) || "this feature",
      message: error.response.statusText || error.message,
    };
  }

  // Handle Error objects with status property
  if (error?.status === 403 || error?.status === 429) {
    return {
      status: error.status as 403 | 429,
      feature: "this feature",
      message: error.message,
    };
  }

  return null;
}

/**
 * Server errorType markers that mean "the user's plan blocks this",
 * as opposed to generic 403s (membership, RLS, auth) which must keep
 * their toast/error UX. Emitted by lib/billing/feature-access.ts and
 * the RESOURCE_LIMIT_EXCEEDED ad-hoc bodies in API routes.
 */
const PLAN_RESTRICTION_ERROR_TYPES = new Set([
  "NO_ACCESS",
  "USAGE_EXCEEDED",
  "LIMIT_EXCEEDED",
  "NO_SUBSCRIPTION",
  "RESOURCE_LIMIT_EXCEEDED",
  "INSUFFICIENT_CREDITS",
]);

/**
 * Detect a plan restriction in an HttpError thrown by safeFetch.
 * Requires a 403/429 status AND a body with a known plan-restriction
 * errorType — a plain `{ error }` 403 (auth/membership failure) is
 * not a plan restriction and returns null. A 402 counts only with
 * errorType INSUFFICIENT_CREDITS (the org's credits can't cover it).
 */
export function planRestrictionFromHttpError(
  error: unknown,
): PlanRestrictionError | null {
  if (!(error instanceof HttpError)) return null;
  if (error.status !== 402 && error.status !== 403 && error.status !== 429) {
    return null;
  }
  if (typeof error.body !== "object" || error.body === null) return null;

  const body = error.body as Partial<FeatureAccessError> & {
    resource?: unknown;
    creditsNeeded?: unknown;
    balance?: unknown;
    creditsEligible?: unknown;
  };
  if (
    typeof body.errorType !== "string" ||
    !PLAN_RESTRICTION_ERROR_TYPES.has(body.errorType)
  ) {
    return null;
  }
  if (
    error.status === 402 &&
    String(body.errorType) !== "INSUFFICIENT_CREDITS"
  ) {
    return null;
  }

  const feature =
    typeof body.featureName === "string"
      ? body.featureName
      : typeof body.feature === "string"
        ? getFeatureDisplayName(body.feature)
        : typeof body.resource === "string"
          ? body.resource
          : "this feature";

  const upgradeOptions = Array.isArray(body.upgradeOptions)
    ? body.upgradeOptions.filter((o): o is string => typeof o === "string")
    : undefined;

  // RESOURCE_LIMIT_EXCEEDED bodies don't carry feature/featureName, so
  // they don't conform to FeatureAccessError — omit details for them.
  const details =
    typeof body.feature === "string" && typeof body.featureName === "string"
      ? (body as FeatureAccessError)
      : undefined;

  return {
    status: error.status as 402 | 403 | 429,
    feature,
    planRequired:
      typeof body.requiredPlan === "string"
        ? body.requiredPlan
        : upgradeOptions?.[0],
    message: typeof body.error === "string" ? body.error : error.message,
    details,
    errorType: body.errorType,
    resource: typeof body.resource === "string" ? body.resource : undefined,
    creditsNeeded:
      typeof body.creditsNeeded === "number" ? body.creditsNeeded : undefined,
    balance: typeof body.balance === "number" ? body.balance : undefined,
    creditsEligible:
      typeof body.creditsEligible === "boolean"
        ? body.creditsEligible
        : undefined,
  };
}

/**
 * Restrictions credits get past: a scan the balance can't cover, or a
 * prompt beyond the plan's per-project cap (allowed while the org has
 * credits). The dialog offers "Buy credits" for these, unless the
 * server says the org can't buy credits (the free plan).
 */
export function isCreditsRestriction(error: PlanRestrictionError): boolean {
  if (error.creditsEligible === false) return false;
  return (
    error.errorType === "INSUFFICIENT_CREDITS" ||
    (error.errorType === "RESOURCE_LIMIT_EXCEEDED" &&
      error.resource === "prompts")
  );
}

/**
 * Parse the response body to get detailed error information
 * This should be called when a 403 or 429 response is received
 */
export async function parsePlanRestrictionResponse(
  response: Response,
): Promise<PlanRestrictionError | null> {
  if (response.status !== 403 && response.status !== 429) {
    return null;
  }

  try {
    const body = await response.json();
    const details = body as FeatureAccessError;

    return {
      status: response.status as 403 | 429,
      feature: getFeatureDisplayName(details.feature) || "this feature",
      planRequired: details.upgradeOptions?.[0],
      message: details.error,
      details,
    };
  } catch {
    // If we can't parse the body, fall back to basic info
    return {
      status: response.status as 403 | 429,
      feature: extractFeatureFromUrl(response.url) || "this feature",
      message: response.statusText,
    };
  }
}

/**
 * Check if error is a usage limit exceeded error (429)
 */
export function isUsageLimitExceeded(error: PlanRestrictionError): boolean {
  return (
    error.status === 429 ||
    error.details?.errorType === "USAGE_EXCEEDED" ||
    error.details?.errorType === "LIMIT_EXCEEDED"
  );
}

/**
 * Check if error is a no access error (403)
 */
export function isNoAccessError(error: PlanRestrictionError): boolean {
  return error.status === 403 || error.details?.errorType === "NO_ACCESS";
}

function extractFeatureFromUrl(url?: string): string | null {
  if (!url) return null;

  if (url.includes("content") || url.includes("document")) {
    return "Content Generation";
  }
  if (url.includes("blog")) {
    return "AI Blog Creation";
  }
  if (url.includes("outline")) {
    return "Blog Outline Generation";
  }
  if (url.includes("analytics")) {
    return "Analytics";
  }
  if (url.includes("collaboration")) {
    return "Collaboration Features";
  }

  return null;
}

export function getFeatureDisplayName(feature?: string): string {
  if (!feature) return "this feature";

  const featureMap: { [key: string]: string } = {
    content: "Content Generation",
    content_generations: "Content Generation",
    blog: "AI Blog Creation",
    outline: "Blog Outline Generation",
    analytics: "Analytics",
    evaluations: "Content Evaluations",
    collaboration: "Collaboration Features",
    collaborative_editing: "Collaborative Editing",
    document: "Document Creation",
    publishing_integrations: "Publishing Integrations",
    content_import: "Content Import",
  };

  return featureMap[feature.toLowerCase()] || feature;
}
